import React, { useState, useEffect } from "react";
import Header from "../../../../Componets/Header";
import Loadercomponent from "../../../../Componets/Loader";
import { Card, Table, Typography, Empty, Alert, Row, Col } from "antd";
import { LineChartOutlined } from "@ant-design/icons";
import { getLedgerEntries } from "../../../../Controllers/Accounting/Services/generalLedgerService";
import headers from "../../../../Data/Tables/Accounting/ReportEntry/ProfitLoss/headers";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "antd";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";

const { Text, Title } = Typography;

const safeToLocaleString = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

const ProfitAndLossReport = () => {
  const [loader, setLoader] = useState(true);
  const [header, setHeader] = useState(headers);
  const [data, setData] = useState({});
  const [dates, setDates] = useState({});
  const [isBalanced, setIsBalanced] = useState(true);
  const [unProcessedData, setUnProcessedData] = useState({});

  const accountTypeToCategory = {
    Revenue: "Income",
    Income: "Income",
    "Sales Revenue": "Income",
    "Interest Income": "Income",
    "Other Revenue": "Income",
    "Purchase Discount": "Income",
    "Unrealized Currency Gains": "Income",
    "Realized Currency Gains": "Income",

    Expense: "Expenses",
    "Direct Costs": "Expenses",
    "Direct Expense": "Expenses",
    "Costs of Goods Sold": "Expenses",

    Advertising: "Expenses",
    "Bank Service Charges": "Expenses",
    "Janitorial Expenses": "Expenses",
    "Consulting & Accounting": "Expenses",
    Entertainment: "Expenses",
    "Postage & Delivery": "Expenses",
    "General Expenses": "Expenses",
    Insurance: "Expenses",
    "Legal Expenses": "Expenses",
    Utilities: "Expenses",
    "Automobile Expenses": "Expenses",
    "Office Expenses": "Expenses",
    "Printing & Stationary": "Expenses",
    Rent: "Expenses",
    "Repairs & Maintenance": "Expenses",
    "Wages & Salaries": "Expenses",
    "Dues & Subscriptions": "Expenses",
    "Telephone & Internet": "Expenses",
    Travel: "Expenses",
    "Bad Debts": "Expenses",
    Depreciation: "Expenses",
    "Employee Benefits Expense": "Expenses",
    "Interest Expense": "Expenses",
    "Bank Revaluations": "Expenses",
    "Sales Discount": "Expenses",
  };

  const formdata = (Data) => {
    setDates(Data.date);
  };

  const processProfitAndLossData = (transactions) => {
    const categories = {
      income: {},
      expenses: {},
    };

    let totalDebits = 0;
    let totalCredits = 0;

    transactions.forEach((transaction) => {
      const accountType = transaction.account?.type;
      const accountCategory = accountTypeToCategory[accountType];
      const accountName = transaction.account_name;
      const accountCode = transaction.account_code;

      totalDebits += transaction.debit || 0;
      totalCredits += transaction.credit || 0;

      if (accountCategory && categories[accountCategory.toLowerCase()]) {
        const categoryData = categories[accountCategory.toLowerCase()];

        if (!categoryData[accountType]) {
          categoryData[accountType] = {
            type: accountType,
            accounts: {},
            totalBalance: 0,
          };
        }

        if (!categoryData[accountType].accounts[accountCode]) {
          categoryData[accountType].accounts[accountCode] = {
            code: accountCode,
            name: accountName,
            balance: 0,
            transactions: [],
          };
        }

        const account = categoryData[accountType].accounts[accountCode];

        let balanceChange = 0;
        if (accountCategory === "Income") {
          balanceChange = transaction.credit - transaction.debit;
        } else if (accountCategory === "Expenses") {
          balanceChange = transaction.debit - transaction.credit;
        }

        account.balance += balanceChange;
        account.transactions.push(transaction);
        categoryData[accountType].totalBalance += balanceChange;
      }
    });

    const incomeData = formatCategoryData(categories.income);
    const expensesData = formatCategoryData(categories.expenses);

    const totalIncome = incomeData.reduce(
      (sum, type) => sum + type.totalBalance,
      0,
    );
    const totalExpenses = expensesData.reduce(
      (sum, type) => sum + type.totalBalance,
      0,
    );
    const netProfit = totalIncome - totalExpenses;

    const isAccountingBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

    setIsBalanced(isAccountingBalanced);

    return {
      revenues: formatForTable(incomeData, "Income"),
      costOfGoodsSold: formatForTable(expensesData, "Expenses"),
      incomeTotal: totalIncome,
      expenseTotal: totalExpenses,
      netProfit: netProfit,
      totalDebits,
      totalCredits,
      isBalanced: isAccountingBalanced,
    };
  };

  const formatCategoryData = (categoryData) => {
    return Object.values(categoryData)
      .map((typeData) => ({
        type: typeData.type,
        accounts: Object.values(typeData.accounts).map((account) => ({
          ...account,
          key: account.code,
        })),
        totalBalance: typeData.totalBalance,
      }))
      .filter((typeData) => typeData.accounts.length > 0);
  };

  const formatForTable = (categoryData, categoryType) => {
    const result = [];

    categoryData.forEach((typeData) => {
      typeData.accounts.forEach((account) => {
        result.push({
          name: account.name,
          total:
            categoryType === "Income"
              ? account.balance
              : -Math.abs(account.balance),
          accountCode: account.code,
        });
      });

      result.push({
        name: `Total ${typeData.type}`,
        total:
          categoryType === "Income"
            ? typeData.totalBalance
            : -Math.abs(typeData.totalBalance),
        isSubTotal: true,
      });
    });

    return result;
  };

  const loadData = async () => {
    setLoader(true);
    try {
      const requestData = {
        source: "Profit-Loss",
        sort: "date",
        ...(dates.start &&
          dates.end && { date: { start: dates.start, end: dates.end } }),
      };

      const response = await getLedgerEntries(requestData);

      setUnProcessedData(response);

      if (response?.success) {
        const processedData = processProfitAndLossData(
          response.data.transactions || [],
        );
        setData(processedData);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoader(false);
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    const firstTransaction = unProcessedData?.data?.transactions[0];
    const branch = firstTransaction?.branch || {};
    const branchName = branch.name || "Company Name";
    const branchEmail = branch.email || "email@company.com";
    const branchPhone = branch.phone || "+968-XXXXXXX";
    const branchCountry = branch.country || "Oman";

    doc.setFontSize(22);
    doc.text(branchName, pageWidth / 2, 20, { align: "center" });

    doc.setFontSize(11);
    doc.text(
      `${branchEmail} | ${branchPhone} | ${branchCountry}`,
      pageWidth / 2,
      28,
      { align: "center" },
    );

    doc.setFontSize(18);
    doc.text("Profit & Loss Statement", pageWidth / 2, 38, { align: "center" });

    if (dates.start && dates.end) {
      doc.setFontSize(11);
      doc.text(
        `Period: ${dayjs(dates.start).format("MMM D, YYYY")} - ${dayjs(
          dates.end,
        ).format("MMM D, YYYY")}`,
        pageWidth / 2,
        46,
        { align: "center" },
      );
    }

    let currentY = 54;

    autoTable(doc, {
      head: [["Account", "Account Code", "Amount"]],
      body: revenues.map((item) => [
        item.name,
        item.accountCode || "",
        safeToLocaleString(item.total),
      ]),
      startY: currentY,
      theme: "grid",
      headStyles: { fillColor: [24, 144, 255] },
    });

    autoTable(doc, {
      head: [["Account", "Account Code", "Amount"]],
      body: costOfGoodsSold.map((item) => [
        item.name,
        item.accountCode || "",
        safeToLocaleString(item.total),
      ]),
      startY: doc.lastAutoTable.finalY + 10,
      theme: "grid",
      headStyles: { fillColor: [24, 144, 255] },
    });

    let summaryY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(12);
    doc.text(`Total Income: ${safeToLocaleString(incomeTotal)}`, 14, summaryY);
    doc.text(
      `Total Expenses: (${safeToLocaleString(Math.abs(expenseTotal))})`,
      14,
      summaryY + 8,
    );
    doc.text(`Net Profit: ${safeToLocaleString(netProfit)}`, 14, summaryY + 16);

    doc.setFontSize(11);
    if (isBalanced) {
      doc.setTextColor(0, 128, 0);
      doc.text("✔ Accounts Balanced", 14, summaryY + 26);
    } else {
      doc.setTextColor(200, 0, 0);
      doc.text("✘ Accounts Imbalanced", 14, summaryY + 26);
    }

    doc.save("Profit_Loss_Report.pdf");
  };

  const exportExcel = () => {
    const rows = [];

    // Revenue & Income
    rows.push(["PROFIT & LOSS REPORT"]);
    rows.push([]);

    if (dates.start && dates.end) {
      rows.push([
        `Period: ${dayjs(dates.start).format("MMM D, YYYY")} - ${dayjs(
          dates.end,
        ).format("MMM D, YYYY")}`,
      ]);
      rows.push([]);
    }

    rows.push(["REVENUE & INCOME"]);
    rows.push(["Account", "Account Code", "Amount"]);

    revenues.forEach((item) => {
      rows.push([
        item.name,
        item.accountCode || "",
        Number(item.total || 0).toFixed(3),
      ]);
    });

    rows.push(["Total Revenue & Income", "", incomeTotal.toFixed(3)]);
    rows.push([]);

    // Expenses
    rows.push(["EXPENSES"]);
    rows.push(["Account", "Account Code", "Amount"]);

    costOfGoodsSold.forEach((item) => {
      rows.push([
        item.name,
        item.accountCode || "",
        Number(item.total || 0).toFixed(3),
      ]);
    });

    rows.push(["Total Expenses", "", expenseTotal.toFixed(3)]);

    rows.push([]);
    rows.push(["SUMMARY"]);
    rows.push(["Total Income", incomeTotal.toFixed(3)]);
    rows.push(["Total Expenses", expenseTotal.toFixed(3)]);
    rows.push(["Net Profit", netProfit.toFixed(3)]);
    rows.push(["Total Debits", totalDebits.toFixed(3)]);
    rows.push(["Total Credits", totalCredits.toFixed(3)]);
    rows.push([
      "Status",
      accountingBalanced ? "Accounts Balanced" : "Accounts Imbalanced",
    ]);

    const content = rows.map((row) => row.join("\t")).join("\n");

    const blob = new Blob([content], {
      type: "application/vnd.ms-excel",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Profit_Loss_Report_${dayjs().format("YYYY-MM-DD")}.xls`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const headerReset = () => {
    let componentData = [];
    headers?.components?.map((value, index) => {
      componentData?.push({
        ...value,
        value: null,
      });
    });
    setHeader({ ...headers, components: componentData });
  };

  useEffect(() => {
    loadData();
    headerReset();
  }, []);

  useEffect(() => {
    loadData();
  }, [dates]);

  const {
    revenues = [],
    costOfGoodsSold = [],
    incomeTotal = 0,
    expenseTotal = 0,
    netProfit = 0,
    totalDebits = 0,
    totalCredits = 0,
    isBalanced: accountingBalanced,
  } = data;

  const detailedColumns = [
    {
      title: "Account",
      dataIndex: "name",
      render: (text, record) => (
        <Text strong={record.isSubTotal || record.isTotal}>
          {record.isSubTotal ? `Total ${text}` : text}
        </Text>
      ),
    },
    {
      title: "Account Code",
      dataIndex: "accountCode",
      render: (code, record) => (
        <Text type="secondary" strong={record.isSubTotal || record.isTotal}>
          {record.isSubTotal ? "" : code}
        </Text>
      ),
    },
    {
      title: "Amount",
      dataIndex: "total",
      align: "right",
      render: (value, record) => {
        const safeValue = value || 0;
        return (
          <Text
            strong={record.isSubTotal || record.isTotal}
            type={safeValue >= 0 ? "success" : "danger"}
          >
            {safeValue >= 0
              ? safeToLocaleString(safeValue)
              : `(${safeToLocaleString(Math.abs(safeValue))})`}
          </Text>
        );
      },
    },
  ];

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <div className="flex gap-3 justify-end mt-4">
        <Button
          onClick={exportExcel}
          className="text-medium form-save-button"
          type="primary"
        >
          Export as Excel
        </Button>

        <Button
          onClick={exportPDF}
          className="text-medium form-save-button"
          type="primary"
        >
          Export as PDF
        </Button>
      </div>
      <Loadercomponent loader={loader}>
        <ScrollWrapper>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <LineChartOutlined />
                <span>Profit & Loss Statement</span>
              </div>
            }
          >
            {!loader && data ? (
              <>
                <div style={{ marginBottom: 16, textAlign: "center" }}>
                  <Text strong>
                    {dates.start && dates.end
                      ? `Period: ${dayjs(dates.start).format(
                          "MMM D, YYYY",
                        )} - ${dayjs(dates.end).format("MMM D, YYYY")}`
                      : ""}
                  </Text>
                </div>

                {!accountingBalanced && (
                  <Alert
                    message="Accounting Imbalance Detected"
                    description={`Total Debits (${safeToLocaleString(
                      totalDebits,
                    )}) do not equal Total Credits (${safeToLocaleString(
                      totalCredits,
                    )})`}
                    type="error"
                    style={{ marginBottom: 16 }}
                    showIcon
                  />
                )}

                {accountingBalanced && (
                  <Alert
                    message="Accounts are Balanced"
                    description="Total Debits equal Total Credits"
                    type="success"
                    style={{ marginBottom: 16 }}
                    showIcon
                  />
                )}

                <Card
                  size="small"
                  title="Revenue & Income"
                  style={{ marginBottom: 16 }}
                  headStyle={{ backgroundColor: "#f6ffed" }}
                >
                  <Table
                    dataSource={revenues.map((item, index) => ({
                      key: `income_${index}`,
                      ...item,
                    }))}
                    columns={detailedColumns}
                    pagination={false}
                    size="small"
                    showHeader={true}
                  />
                  <div
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#f6ffed",
                      borderTop: "1px solid #b7eb8f",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    <Text strong>
                      Total Revenue & Income: {safeToLocaleString(incomeTotal)}
                    </Text>
                  </div>
                </Card>

                <Card
                  size="small"
                  title="Expenses"
                  style={{ marginBottom: 16 }}
                  headStyle={{ backgroundColor: "#fff2e8" }}
                >
                  <Table
                    dataSource={costOfGoodsSold.map((item, index) => ({
                      key: `exp_${index}`,
                      ...item,
                    }))}
                    columns={detailedColumns}
                    pagination={false}
                    size="small"
                    showHeader={true}
                  />
                  <div
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#fff2e8",
                      borderTop: "1px solid #ffbb96",
                      textAlign: "right",
                      fontWeight: "bold",
                    }}
                  >
                    <Text strong>
                      Total Expenses: (
                      {safeToLocaleString(Math.abs(expenseTotal))})
                    </Text>
                  </div>
                </Card>

                <Card
                  style={{
                    marginBottom: 16,
                    border: "2px solid #d9d9d9",
                  }}
                  bodyStyle={{ padding: "16px" }}
                >
                  <Row gutter={16} justify="space-between" align="middle">
                    <Col>
                      <Title level={4} style={{ margin: 0 }}>
                        Net Profit Calculation
                      </Title>
                    </Col>
                    <Col>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ marginBottom: 8 }}>
                          <Text>Total Income: </Text>
                          <Text strong type="success">
                            {safeToLocaleString(incomeTotal)}
                          </Text>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <Text>Total Expenses: </Text>
                          <Text strong type="danger">
                            ({safeToLocaleString(Math.abs(expenseTotal))})
                          </Text>
                        </div>
                        <hr style={{ margin: "8px 0" }} />
                        <div>
                          <Text strong>Net Profit: </Text>
                          <Text
                            strong
                            style={{ fontSize: "18px" }}
                            type={netProfit >= 0 ? "success" : "danger"}
                          >
                            {safeToLocaleString(netProfit)}
                          </Text>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>

                <Card size="small" title="Accounting Summary">
                  <Row gutter={16}>
                    <Col span={12}>
                      <div style={{ textAlign: "center" }}>
                        <Text strong>Total Debits</Text>
                        <div
                          style={{
                            fontSize: "16px",
                            color: "#ff4d4f",
                            fontWeight: "bold",
                          }}
                        >
                          {safeToLocaleString(totalDebits)}
                        </div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ textAlign: "center" }}>
                        <Text strong>Total Credits</Text>
                        <div
                          style={{
                            fontSize: "16px",
                            color: "#52c41a",
                            fontWeight: "bold",
                          }}
                        >
                          {safeToLocaleString(totalCredits)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <div
                    style={{
                      marginTop: 16,
                      padding: "8px",
                      backgroundColor: accountingBalanced
                        ? "#f6ffed"
                        : "#fff2e8",
                      border: `1px solid ${
                        accountingBalanced ? "#b7eb8f" : "#ffbb96"
                      }`,
                      borderRadius: "4px",
                      textAlign: "center",
                    }}
                  >
                    <Text
                      strong
                      type={accountingBalanced ? "success" : "danger"}
                    >
                      {accountingBalanced
                        ? "✓ Accounts are Balanced"
                        : "✗ Accounts are Imbalanced"}
                    </Text>
                  </div>
                </Card>
              </>
            ) : (
              <Empty
                description={loader ? "Loading report..." : "No data available"}
              />
            )}
          </Card>
        </ScrollWrapper>
      </Loadercomponent>
    </Header>
  );
};

export default ProfitAndLossReport;
